// Billing.js (Updated for URL Query Parameter Tabs & Navigation)
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Import the child components
import Overview from './Overview';
// import InvoiceHistory from './InvoiceHistory'; // Uncomment if needed
import PlanSelectionPage from './PlanSelectionPage';
import PaymentHistory from './PaymentHistory';
import PaymentMethod from './PaymentMethod';
import SubscriptionLog from "./SubscriptionLog";

// --- Define tab slugs (keep consistent) ---
const tabSlugs = [
    'overview',
    'cards',
    'plan',
    'subscriptions',
    'payments'
];

// --- TabPanel and a11yProps (Keep as they are) ---
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            style={{ width: '100%' }}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}
TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};
function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        "aria-controls": `vertical-tabpanel-${index}`,
    };
}

// --- Main Billing Component ---
export default function Billing() {

    useEffect(() => {
        document.title = 'Billing | KlonIT Web Application';
    }, []);

    const [searchParams, setSearchParams] = useSearchParams();

    // Function to get tab index from current searchParams
    // Encapsulated for reuse
    const getTabIndexFromUrl = useCallback(() => {
        const tabQuery = searchParams.get('tab')?.toLowerCase();
        if (tabQuery) {
            const index = tabSlugs.indexOf(tabQuery);
            if (index !== -1) {
                return index;
            }
        }
        return 0; // Default to 0 if param missing or invalid
    }, [searchParams]); // Depends on searchParams

    // State for the currently selected tab index
    const [value, setValue] = useState(getTabIndexFromUrl); // Initialize state from URL

    // State for Snackbar
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // --- Effect 1: Synchronize Tab State with URL Changes ---
    // Runs whenever searchParams changes (e.g., browser back/forward, manual URL edit)
    useEffect(() => {
        const indexFromUrl = getTabIndexFromUrl();
        console.log(`URL tab changed. Current value: ${value}, Index from URL: ${indexFromUrl}`); // Debug log
        if (indexFromUrl !== value) {
            console.log(`Updating tab value from ${value} to ${indexFromUrl}`); // Debug log
            setValue(indexFromUrl); // Update the selected tab state
        }
        // We also need to ensure the URL has *some* valid tab slug if none is present
        // This handles cases where the user navigates to just /billing
        const currentTabSlugInUrl = searchParams.get('tab')?.toLowerCase();
        if (!currentTabSlugInUrl || tabSlugs.indexOf(currentTabSlugInUrl) === -1) {
             // If the URL has no tab or an invalid one, set it based on the current state (or default 0)
             // Use replace to avoid cluttering history when correcting the URL
             setSearchParams({ tab: tabSlugs[value] }, { replace: true });
             console.log(`Corrected URL to include tab: ${tabSlugs[value]}`); // Debug log
        }

    }, [searchParams, value, getTabIndexFromUrl, setSearchParams]); // Depend on searchParams and value


    // --- Handlers ---
    const handleChange = (event, newValue) => {
        // This function handles USER CLICKS on tabs
        if (newValue !== value) { // Prevent unnecessary updates if clicking the already active tab
             setValue(newValue);
             // Update URL query parameter when tab is clicked
             const newSlug = tabSlugs[newValue];
             setSearchParams({ tab: newSlug }); // Update URL, creates new history entry
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const showMessage = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    // --- Callbacks for PlanPage ---
    const handlePlanChangeComplete = useCallback((successMessage) => {
        showMessage(successMessage || "Subscription updated successfully!", "success");
        const overviewIndex = tabSlugs.indexOf('overview');
        if (overviewIndex !== -1) {
            // Both state and URL need update
            setValue(overviewIndex);
            setSearchParams({ tab: 'overview' }); // Navigate by updating URL
        }
    }, [setSearchParams]);

     const handlePlanChangeError = useCallback((errorMessage) => {
        showMessage(errorMessage || "An error occurred while updating the subscription.", "error");
     }, []);

    // --- Render ---
    console.log("Rendering Billing component with value:", value); // Debug log

    return (
        <Box sx={{ display: 'flex', flexGrow: 1, mt: { xs: 0, md: 3  } }} >
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                   Billing Management
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                    {/* Left Column (Tabs) */}
                    <Box sx={{
                        borderColor: 'divider',
                        mb: { xs: 2, md: 0 },
                        mr: { xs: 0, md: 2 },
                        minWidth: '180px'
                    }}>
                         <Tabs
                            orientation="vertical"
                            variant="scrollable"
                            value={value} // Controlled by state
                            onChange={handleChange} // Updates state & URL on click
                            aria-label="Vertical billing tabs"
                            sx={{
                                borderRight: 1,
                                borderColor: "divider",
                             }}
                         >
                            <Tab label="Overview" {...a11yProps(0)} />
                            <Tab label="Manage Cards" {...a11yProps(1)} />
                            <Tab label="Manage Plan" {...a11yProps(2)} />
                            <Tab label="Subscription History" {...a11yProps(3)} />
                            <Tab label="Payment History" {...a11yProps(4)} />
                         </Tabs>
                    </Box>

                    {/* Right Column (Tab Content) */}
                    <Box sx={{ flexGrow: 1, width: '100%' }}>
                        {/* TabPanel visibility is controlled by the 'value' state */}
                        <TabPanel value={value} index={0}>
                             {/* Using key ensures Overview remounts/refreshes if its internal useEffect depends on mount */}
                             <Overview key={`overview-tab`} />
                        </TabPanel>
                        <TabPanel value={value} index={1}>
                             <PaymentMethod />
                        </TabPanel>
                        <TabPanel value={value} index={2}>
                             <PlanSelectionPage
                                onComplete={handlePlanChangeComplete}
                                onError={handlePlanChangeError}
                            />
                        </TabPanel>
                        <TabPanel value={value} index={3}>
                             <SubscriptionLog />
                        </TabPanel>
                        <TabPanel value={value} index={4}>
                             <PaymentHistory />
                        </TabPanel>
                    </Box>
                </Box>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}